/**
 * Table widget — records from a layer with chosen columns.
 *
 * Sorting and filtering are pushed to the service (ArcGIS `orderByFields`,
 * WFS `sortBy` + `CQL_FILTER`) so paging never depends on holding the whole
 * layer in memory. Only the current page is fetched.
 */

import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useSourceFields } from './useSourceFields';
import { useWidgetData } from './useWidgetData';
import WidgetFrame from './WidgetFrame';
import {
  combineWhere,
  useIncomingConstraint,
  type ActionSourceProps,
  type ActionTargetProps,
  useOutgoingSignal,
} from './useActions';
import { getDataSourceAdapter, type RecordResult } from '../services/dashboardDataService';
import { buildWhereClause, formatCellValue, validateWidget } from '../utils/dashboardUtils';
import type { DashboardDataSource, DashboardWidget } from '../types/dashboardTypes';

interface Props {
  widget: DashboardWidget;
  source?: DashboardDataSource;
  refreshInterval: number;
  accentColor: string;
  dividerColor?: string;
  selectorWhere?: string;
}

export default function TableWidget({
  widget,
  source,
  refreshInterval,
  accentColor,
  dividerColor,
  selectorWhere,
  incomingSignals,
  onClearSignals,
  activeSignal,
  onSignal,
}: Props & ActionTargetProps & ActionSourceProps) {
  const { fields } = useSourceFields(source);
  const problems = validateWidget(widget);
  const [page, setPage] = useState(0);

  const columns = widget.config.columns ?? [];
  const pageSize = widget.config.pageSize ?? 10;
  const incoming = useIncomingConstraint(incomingSignals, fields);
  const where = combineWhere(combineWhere(buildWhereClause(widget.filters, fields), incoming.where), selectorWhere ?? '');
  /* Selections leave through here so a spatially-matched action can attach
     the selected shape. Attribute-matched actions pass straight through. */
  const emitSignal = useOutgoingSignal(widget, source, onSignal, where);
  const enabled = Boolean(source) && problems.length === 0;

  // The services expose a record COUNT, not cursor paging, so a page is read as
  // "first (page+1)×size rows" and the tail is sliced off. Fine at dashboard
  // page sizes; a server-side offset would be the upgrade for very deep paging.
  const fetchCount = pageSize * (page + 1);

  const { data, status, error, reload } = useWidgetData<RecordResult>(
    () =>
      getDataSourceAdapter(source as DashboardDataSource).fetchRecords(
        source as DashboardDataSource,
        {
          columns,
          where,
          extent: incoming.extent, geometry: incoming.geometry,
          limit: fetchCount,
          orderByField: widget.config.orderByField,
          orderDirection: widget.config.orderDirection,
        },
      ),
    `table|${source?.id}|${columns.join(',')}|${where}|${JSON.stringify(incoming.extent ?? null)}|${incoming.geometry ? 'geom' : ''}|${fetchCount}|${widget.config.orderByField ?? ''}|${widget.config.orderDirection ?? ''}`,
    refreshInterval,
    enabled,
  );

  /* A row broadcasts the value of the FIRST column, which is the table's own
     leading identifier — there is no separate key concept in the config, and
     asking the admin to nominate one for a feature they may not use would be
     noise. Clicking the selected row again clears the selection. */
  const selectionColumn = columns[0] ?? '';
  const canSelect =
    Boolean(onSignal) &&
    Boolean(selectionColumn) &&
    (widget.actions ?? []).some(
      (action) => action.trigger === 'selection' && action.targetWidgetIds.length > 0,
    );
  const selectedValue =
    activeSignal && activeSignal.kind === 'selection' ? activeSignal.value : null;
  const selectRow = (record: Record<string, unknown>) => {
    const value = formatCellValue(record[selectionColumn]);
    if (selectedValue === value) {
      onSignal?.(widget.id, null);
      return;
    }
    emitSignal(widget.id, {
      kind: 'selection',
      sourceId: widget.id,
      sourceTitle: widget.title,
      field: selectionColumn,
      value,
    });
  };

  const allRows = data?.records ?? [];
  const rows = allRows.slice(page * pageSize, (page + 1) * pageSize);
  const total = data?.total ?? null;
  const hasNext = total === null ? allRows.length >= fetchCount : (page + 1) * pageSize < total;

  const aliasFor = (name: string) => fields.find((field) => field.name === name)?.alias || name;

  return (
    <WidgetFrame
      title={widget.title}
      status={status}
      error={error}
      onRetry={reload}
      incomplete={problems}
      accentColor={accentColor}
      outlineColor={widget.config.outlineColor ?? dividerColor}
      actionLabels={incoming.labels}
      onClearActions={() => onClearSignals?.(incoming.sourceIds)}
      footnote={[
        source?.name,
        total !== null ? `${total.toLocaleString()} record${total === 1 ? '' : 's'}` : '',
      ]
        .filter(Boolean)
        .join(' · ')}
    >
      <div className="flex h-full min-h-0 flex-col">
        <div className="min-h-0 flex-1 overflow-auto">
          {rows.length === 0 ? (
            <p className="p-4 text-center text-xs text-slate-400">No records match this filter</p>
          ) : (
            <table className="w-full border-collapse text-left text-[11px]">
              <thead className="sticky top-0 z-10 bg-slate-50">
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column}
                      className="whitespace-nowrap border-b border-slate-200 px-2.5 py-1.5 font-semibold text-slate-500"
                    >
                      {aliasFor(column)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((record, index) => {
                  const isSelected =
                    canSelect && selectedValue === formatCellValue(record[selectionColumn]);
                  return (
                  <tr
                    key={index}
                    onClick={canSelect ? () => selectRow(record) : undefined}
                    aria-selected={canSelect ? isSelected : undefined}
                    className={`border-b border-slate-50 hover:bg-slate-50 ${
                      canSelect ? 'cursor-pointer' : ''
                    } ${isSelected ? 'bg-blue-50' : ''}`}
                  >
                    {columns.map((column) => (
                      <td
                        key={column}
                        className="max-w-[220px] truncate px-2.5 py-1.5 text-slate-700"
                        title={formatCellValue(record[column])}
                      >
                        {formatCellValue(record[column])}
                      </td>
                    ))}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {(page > 0 || hasNext) && (
          <div className="flex flex-shrink-0 items-center justify-between border-t border-slate-100 px-2.5 py-1">
            <span className="text-[10px] text-slate-400">Page {page + 1}</span>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => setPage((current) => Math.max(0, current - 1))}
                disabled={page === 0}
                aria-label="Previous page"
                className="rounded p-1 text-slate-500 transition hover:bg-slate-100 disabled:opacity-30"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setPage((current) => current + 1)}
                disabled={!hasNext}
                aria-label="Next page"
                className="rounded p-1 text-slate-500 transition hover:bg-slate-100 disabled:opacity-30"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </WidgetFrame>
  );
}
